import type { SubscriptionTier as SubscriptionTierContract } from '@optimus/models';

// eslint-disable-next-line no-shadow
export enum SubscriptionTier {
  EDGE = 'edge',
  SRT = 'srt',
  ADV = 'adv',
  ADVP = 'advp',
  AM = 'am',
  CC = 'cc',
  PIN = 'pin',
  RCC = 'rcc',
  SNA = 'sna',
}

/**
 * Matching API Response from membership Level with SubscriptionTier enum values.
 * Keep this browser-local and in sync with `snMembershipLevelToSubscriptionTier`
 * in `@optimus/models` — a runtime import from that package exposes untranspiled
 * workspace TypeScript to Create React App's Webpack config. The `satisfies`
 * clause type-checks the values against the backend contract, so a tier renamed
 * there fails the build here instead of drifting silently.
 */
export const snMembershipLevelToSubscriptionTier: Record<string, SubscriptionTier> = {
  pinnacle: SubscriptionTier.PIN,
  alliance: SubscriptionTier.SNA,
  cc: SubscriptionTier.CC,
  rcc: SubscriptionTier.RCC,
  advanced: SubscriptionTier.ADV,
  am: SubscriptionTier.AM,
  srt: SubscriptionTier.SRT,
} satisfies Record<string, `${SubscriptionTierContract}`>;

/**
 * The `Plan` property sent to Heap for each tier. Keep in sync with
 * `subscriptionTierToHeapPlan` in `@optimus/models` (browser-local for the same
 * reason as the map above).
 */
export const subscriptionTierToHeapPlan: Record<SubscriptionTier, string> = {
  [SubscriptionTier.EDGE]: 'Edge',
  [SubscriptionTier.SRT]: 'SRT',
  [SubscriptionTier.ADV]: 'Advanced',
  [SubscriptionTier.ADVP]: 'Advanced',
  [SubscriptionTier.AM]: 'AM',
  [SubscriptionTier.CC]: 'CC',
  [SubscriptionTier.PIN]: 'Pinnacle',
  [SubscriptionTier.RCC]: 'RCC',
  [SubscriptionTier.SNA]: 'Alliance',
} satisfies Record<`${SubscriptionTierContract}`, string>;

/**
 * Heap `Plan` for a tier, or undefined while the tier is not known yet.
 */
export const getHeapPlan = (tier?: SubscriptionTier | null): string | undefined => (
  tier ? subscriptionTierToHeapPlan[tier] : undefined
);

/**
 * Heap `Plan` for a loaded member's company. Mirrors useHasAccess: a non-SN
 * company without a subscription row is a free Edge member, while an SN
 * company without a tier is still unknown (its tier comes from the SN API).
 */
export const resolveHeapPlan = ({
  company,
  isServiceNation,
}: {
  company?: { id?: string; subscription?: { subscriptionTier?: SubscriptionTier } | null } | null;
  isServiceNation: boolean;
}): string | undefined => {
  if (!company?.id) {
    return undefined;
  }
  const tier = company.subscription?.subscriptionTier;
  if (tier) {
    return getHeapPlan(tier);
  }
  return isServiceNation ? undefined : getHeapPlan(SubscriptionTier.EDGE);
};

// eslint-disable-next-line no-shadow
export enum SubscriptionStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  CANCELED = 'canceled',
}
